import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  LinearProgress,
  Card,
  CardContent,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Cancel as CancelIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { downloadAttachment, deleteAttachment, downloadLeadFile } from '../../store/slices/documentsSlice';
import { documentsAPI } from '../../api/documents';
import { validateFile, getFileTypeCategory, formatFileSize } from '../../utils/documents';
import { parseUploadErrorResponse } from '../../utils/errorHandler';
import { toast } from 'react-toastify';
import SweetAlert from '../../utils/sweetAlert.js';

function mapApiAttachmentToRow(doc) {
  const id = doc.id || doc.attachment_id;
  return {
    id,
    document_name: doc.document_name || doc.title || doc.file_name || `attachment-${id}`,
    file_name: doc.file_name || doc.document_name || doc.title || `attachment-${id}`,
    file_size: doc.file_size || 0,
    mime_type: doc.mime_type || doc.type || 'application/octet-stream',
    created_at: doc.created_at || new Date().toISOString(),
    attachment_id: id,
    path: doc.path || doc.file_url,
    type: getFileTypeCategory(doc.mime_type || doc.type || 'application/octet-stream'),
  };
}

const getFilePath = (file) => file?.path || file?.url || file?.file_path || file?.file_url;

/**
 * Resumable uploads to /attachments with model_type Lead (same pattern as merchant DocsStep).
 * Reports attachment IDs upward for the lead create/update payload.
 */
export default function LeadAttachmentsUpload({
  leadId = null,
  disabled = false,
  readOnly = false,
  attachmentIds = [],
  onAttachmentIdsChange,
  /** Increment when lead data is loaded so rows re-hydrate from API */
  hydrateToken = 0,
  /** Full attachment objects from GET lead (for names / download on edit) */
  initialAttachments = null,
}) {
  const dispatch = useDispatch();
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fileUploads, setFileUploads] = useState(new Map());
  const resumableRef = useRef(null);
  const uploadAreaRef = useRef(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const lastProgressUpdate = useRef({});
  const progressTimeouts = useRef({});
  const attachmentIdsRef = useRef(attachmentIds);
  attachmentIdsRef.current = attachmentIds;

  const pushIds = (next) => {
    onAttachmentIdsChange?.(next);
  };

  // Hydrate table from API when opening an existing lead
  useEffect(() => {
    if (readOnly) return;
    if (!hydrateToken || !initialAttachments?.length) return;
    setUploadedFiles(initialAttachments.map(mapApiAttachmentToRow));
  }, [hydrateToken, initialAttachments, readOnly]);

  useEffect(() => {
    if (readOnly) return;
    const initializeUploader = async () => {
      try {
        const r = await documentsAPI.initializeResumableForLead(leadId || null);
        if (uploadAreaRef.current) {
          r.assignBrowse(uploadAreaRef.current);
          r.assignDrop(uploadAreaRef.current);
        }

        r.on('fileAdded', handleFileAdded);
        r.on('fileProgress', handleFileProgress);
        r.on('fileSuccess', handleFileSuccess);
        r.on('fileError', handleFileError);
        r.on('complete', () => setUploading(false));
        r.on('error', (message, file) => {
          const errMsg = parseUploadErrorResponse(message, file?.fileName || file?.file?.name);
          toast.error(errMsg, { toastId: 'upload-error' });
        });

        resumableRef.current = r;
      } catch (error) {
        toast.error(error?.message || 'Failed to initialize file uploader');
      }
    };

    initializeUploader();

    return () => {
      if (resumableRef.current) {
        resumableRef.current.cancel();
        lastProgressUpdate.current = {};
        Object.values(progressTimeouts.current).forEach((timeout) => {
          if (timeout) clearTimeout(timeout);
        });
        progressTimeouts.current = {};
      }
    };
  }, [readOnly, leadId]);

  useEffect(() => {
    if (readOnly || !resumableRef.current) return;
    const id = leadId || null;
    resumableRef.current.opts.query = {
      model_type: 'Lead',
      ...(id ? { model_id: id } : {}),
    };
  }, [leadId, readOnly]);

  const handleFileAdded = (file) => {
    const validation = validateFile(file);
    if (!validation.isValid) {
      toast.error(`File validation failed: ${validation.errors.join(', ')}`, { toastId: 'file-validation' });
      if (resumableRef.current) resumableRef.current.removeFile(file);
      return;
    }

    const fileUpload = {
      id: file.uniqueIdentifier,
      file,
      name: file.fileName,
      size: file.size,
      progress: 0,
      status: 'ready',
      error: null,
      type: getFileTypeCategory(file.file?.type || 'application/octet-stream'),
    };

    setFileUploads((prev) => new Map(prev.set(file.uniqueIdentifier, fileUpload)));
    setUploading(true);

    setTimeout(() => {
      if (resumableRef.current) resumableRef.current.upload();
      else toast.error('Upload service not ready. Please refresh and try again.');
    }, 100);
  };

  const handleFileProgress = (file) => {
    const progress = Math.floor(file.progress() * 100);
    const fileId = file.uniqueIdentifier;

    if (progressTimeouts.current[fileId]) {
      clearTimeout(progressTimeouts.current[fileId]);
    }

    progressTimeouts.current[fileId] = setTimeout(() => {
      const now = Date.now();
      const lastUpdate = lastProgressUpdate.current[fileId];

      if (
        !lastUpdate ||
        now - lastUpdate > 1000 ||
        progress - (lastProgressUpdate.current[`${fileId}_progress`] || 0) >= 5
      ) {
        lastProgressUpdate.current[fileId] = now;
        lastProgressUpdate.current[`${fileId}_progress`] = progress;

        setFileUploads((prev) => {
          const newMap = new Map(prev);
          const fileUpload = newMap.get(fileId);
          if (fileUpload) {
            newMap.set(fileId, { ...fileUpload, progress, status: 'uploading' });
          }
          return newMap;
        });
      }
    }, 200);
  };

  const handleFileSuccess = (file, message) => {
    try {
      const response = typeof message === 'string' ? JSON.parse(message) : message;

      const attachmentId =
        response?.data?.id ||
        response?.id ||
        response?.data?.attachment?.id ||
        response?.attachment?.id ||
        response?.data?.data?.id ||
        response?.attachment_id ||
        response?.data?.attachment_id;

      const filePath = response?.data?.path;

      setFileUploads((prev) => {
        const newMap = new Map(prev);
        newMap.delete(file.uniqueIdentifier);
        return newMap;
      });

      const newFile = {
        id: attachmentId || file.uniqueIdentifier,
        path: filePath,
        document_name: file.fileName,
        file_name: file.fileName,
        file_size: file.size,
        mime_type: file.file.type || 'application/octet-stream',
        created_at: new Date().toISOString(),
        attachment_id: attachmentId,
        resumableId: file.uniqueIdentifier,
        type: getFileTypeCategory(file.file.type || 'application/octet-stream'),
      };

      setUploadedFiles((prev) => [...prev, newFile]);

      if (attachmentId) {
        const next = [...(attachmentIdsRef.current || []), attachmentId];
        attachmentIdsRef.current = next;
        pushIds(next);
      }
    } catch (error) {
      toast.error(error?.message || 'Failed to process upload response');
    }
  };

  const handleFileError = (file, message) => {
    const displayMessage = parseUploadErrorResponse(message, file?.fileName || file?.file?.name);
    toast.error(displayMessage, { toastId: 'upload-error' });

    setFileUploads((prev) => {
      const newMap = new Map(prev);
      const fileUpload = newMap.get(file.uniqueIdentifier);
      if (fileUpload) {
        newMap.set(file.uniqueIdentifier, { ...fileUpload, status: 'error', error: displayMessage });
      }
      return newMap;
    });

    setUploading(false);
  };

  const handleStartUpload = () => {
    if (resumableRef.current) {
      resumableRef.current.upload();
      toast.info('Starting upload...');
    }
  };

  const handleCancelUpload = () => {
    if (resumableRef.current) {
      resumableRef.current.cancel();
      setFileUploads(new Map());
      setUploading(false);
      toast.info('Upload cancelled');
    }
  };

  const handleRemoveFile = (fileId) => {
    const fileToRemove = uploadedFiles.find((f) => f.id === fileId);
    setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
    const next = (attachmentIdsRef.current || []).filter((id) => id !== fileId);
    attachmentIdsRef.current = next;
    pushIds(next);

    setFileUploads((prev) => {
      const newMap = new Map(prev);
      for (const [key, value] of newMap.entries()) {
        if (value.attachmentId === fileId || value.uploadedFileId === fileId || value.id === fileId) {
          newMap.delete(key);
          break;
        }
      }
      if (fileToRemove?.resumableId && newMap.has(fileToRemove.resumableId)) {
        newMap.delete(fileToRemove.resumableId);
      }
      return newMap;
    });

    toast.success('File removed successfully!');
  };

  const handleConfirmAndDelete = async (file) => {
    try {
      const result = await SweetAlert.deleteConfirm(file.document_name || file.file_name || 'this attachment');
      if (!result.isConfirmed) return;

      await dispatch(deleteAttachment(file?.id)).unwrap();
      handleRemoveFile(file.id);
      await SweetAlert.success({ text: 'Attachment deleted successfully.' });
    } catch (error) {
      await SweetAlert.error({
        text: error?.message || 'Failed to delete attachment',
      });
    }
  };

  const handleDownload = async (file) => {
    const filePath = getFilePath(file);
    const attId = file?.id || file?.attachment_id;
    const fileName = file?.document_name || file?.file_name || 'download';

    if (!filePath && !attId) {
      toast.error('No file source available');
      return;
    }

    try {
      setDownloadingId(file.id);

      let blobData;
      let contentType;
      if (filePath) {
        const res = await dispatch(downloadLeadFile(filePath)).unwrap();
        blobData = res.data;
        contentType = res.headers?.['content-type'];
      } else {
        blobData = await dispatch(downloadAttachment(attId)).unwrap();
        contentType = file?.mime_type;
      }

      const blob = new Blob([blobData], {
        type: contentType || 'application/octet-stream',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success(`${fileName} downloaded`);
    } catch (err) {
      toast.error(err.message ?? 'Download failed');
    } finally {
      setDownloadingId(null);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon sx={{ color: 'success.main' }} />;
      case 'error':
        return <ErrorIcon sx={{ color: 'error.main' }} />;
      case 'uploading':
        return <CircularProgress size={16} />;
      default:
        return <UploadIcon sx={{ color: 'info.main' }} />;
    }
  };

  const viewRows = readOnly && initialAttachments?.length
    ? initialAttachments.map(mapApiAttachmentToRow)
    : uploadedFiles;

  if (readOnly) {
    if (!initialAttachments?.length) {
      return null;
    }
    return (
      <Box sx={{ mt: 1 }}>
        <Typography variant="subtitle1" sx={{ color: 'text.secondary', fontWeight: 600, mb: 1 }}>
          Attachments
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell>File Name</TableCell>
                <TableCell>Size</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {viewRows.map((file) => (
                <TableRow key={file.id}>
                  <TableCell>{file.document_name || file.file_name}</TableCell>
                  <TableCell>{formatFileSize(file.file_size)}</TableCell>
                  <TableCell align="right">
                    {downloadingId === file.id ? (
                      <CircularProgress size={16} />
                    ) : (
                      <IconButton
                        size="small"
                        color="secondary"
                        onClick={() => handleDownload(file)}
                        title="Download"
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        Files upload automatically when added (resumable / chunked). Max 10 files. Allowed types match merchant
        documentation uploads.
      </Alert>

      <Card
        ref={uploadAreaRef}
        onDragEnter={() => setDragOver(true)}
        onDragLeave={() => setDragOver(false)}
        onDrop={() => setDragOver(false)}
        sx={{
          border: '2px dashed',
          borderColor: dragOver ? 'primary.main' : 'grey.300',
          bgcolor: dragOver ? 'primary.50' : 'grey.50',
          cursor: disabled || uploading ? 'not-allowed' : 'pointer',
          mb: 2,
          opacity: disabled || uploading ? 0.6 : 1,
          '&:hover': {
            borderColor: disabled ? 'grey.300' : 'primary.main',
            bgcolor: disabled ? 'grey.50' : 'primary.50',
          },
          transition: 'all 0.2s ease',
        }}
      >
        <CardContent sx={{ textAlign: 'center', py: 3 }}>
          <UploadIcon sx={{ fontSize: 40, color: 'grey.400', mb: 1 }} />
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            {uploading ? 'Uploading…' : uploadedFiles.length === 0 ? 'No files yet' : `${uploadedFiles.length} file(s)`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {disabled ? 'Upload disabled' : 'Click to browse or drag files here'}
          </Typography>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Button
          variant="contained"
          size="small"
          startIcon={<UploadIcon />}
          onClick={handleStartUpload}
          disabled={disabled || fileUploads.size === 0 || uploading}
        >
          {uploading ? 'Uploading…' : 'Start upload'}
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<CancelIcon />}
          onClick={handleCancelUpload}
          disabled={disabled || (!uploading && fileUploads.size === 0)}
        >
          Cancel upload
        </Button>
      </Box>

      {fileUploads.size > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Active uploads ({fileUploads.size})
          </Typography>
          {Array.from(fileUploads.values()).map((fileUpload) => (
            <Card key={fileUpload.id} sx={{ mb: 1 }}>
              <CardContent sx={{ py: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                  {getStatusIcon(fileUpload.status)}
                  <Typography variant="body2" sx={{ flexGrow: 1 }}>
                    {fileUpload.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatFileSize(fileUpload.size)}
                  </Typography>
                </Box>
                <LinearProgress variant="determinate" value={fileUpload.progress} sx={{ mb: 0.5 }} />
                {fileUpload.error && (
                  <Typography variant="caption" color="error">
                    {fileUpload.error}
                  </Typography>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell>File Name</TableCell>
              <TableCell>Size</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {uploadedFiles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    No attachments yet
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              uploadedFiles.map((file) => (
                <TableRow key={file.id}>
                  <TableCell>{file.document_name || file.file_name}</TableCell>
                  <TableCell>{formatFileSize(file.file_size)}</TableCell>
                  <TableCell>
                    <Chip label={file.type || 'other'} size="small" />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {(getFilePath(file) || file?.id || file?.attachment_id) &&
                        (downloadingId === file.id ? (
                          <CircularProgress size={16} />
                        ) : (
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => handleDownload(file)}
                            title="Download"
                            disabled={disabled}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        ))}
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleConfirmAndDelete(file)}
                        title="Delete"
                        disabled={disabled}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
